import { REGION } from '@ms/config/app'
import cookie from '@ms/utils/cookie'

/**
 * app webview 内会注入以下 JSON
 * window.msUserAgent = {
    appName: '', // msport, msportGames, msportCode
    appVersion: '', // eg. 1.4.2
    platform: '', // ios, android
    store: '', // apple, gp，apk
    clientId: '' // app-wv, games-wv
    theme: '' // 1深色MSport，2深色Chelsea，3深色bvb，4白色MSport，5白色Chelsea，6白色bvb
  }
 */

export const isAndroid = () => /Android/i.test(navigator.userAgent)
export const isIOS = () => /\(i[^;]+;( U;)? CPU.+Mac OS X/.test(navigator.userAgent)
export const isSafari = () => /Safari/i.test(navigator.userAgent) && !/Chrome|CriOS/i.test(navigator.userAgent)
/**
 * Checks if the current device is an Apple device (iPhone, iPad, iPod, or macOS).
 *
 * @returns {boolean} `true` if the device is an Apple device, `false` otherwise.
 */
export const isAppleDevice = () => /iPhone|iPad|iPod|Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.userAgent)

export function getIOSVersion() {
  if (!isIOS()) return ''
  const userAgent = navigator.userAgent
  const match = userAgent.match(/OS (\d+)_?(\d+)?_?(\d+)?/)

  if (match) {
    const major = parseInt(match[1]) // 主版本号
    const minor = parseInt(match[2] || '0') // 次版本号
    const patch = parseInt(match[3] || '0') // 补丁版本号
    return {
      major,
      minor,
      patch
    }
  } else {
    return null // 不是 iOS 系统
  }
}

export function isOperaMini() {
  return /Opera Mini/i.test(navigator.userAgent)
}

/**
 * Checks if the current environment supports class static fields.
 *
 * This function attempts to create a class with a static field using the `Function` constructor.
 * If the creation is successful, it returns `true`, indicating that the environment supports class static fields.
 * If an error is thrown during the creation, it catches the error, logs it to the console, and returns `false`.
 *
 * @returns {boolean} `true` if the environment supports class static fields, `false` otherwise.
 */
export const isSupportClassStatic = () => {
  try {
    new Function(`class Abc { static x = '1' }`)
    return true
  } catch (e) {
    console.error(e)
    return false
  }
}

const isOldMsportApp = () => window.navigator.userAgent.indexOf('msport/app') !== -1 || !!cookie.get('appVersion')
const isOldGamesApp = () => window.navigator.userAgent.indexOf('msportGames/app') !== -1
const isOldCodeApp = () => window.navigator.userAgent.indexOf('msportCode/app') !== -1

const getOldAppName = () => {
  let appName = ''
  if (isOldMsportApp()) {
    appName = 'msport'
  } else if (isOldGamesApp()) {
    appName = 'msportGames'
  } else {
    appName = 'msportCode'
  }
  return appName
}

const getOldAppVersion = () => {
  const cookieVersionMatched = cookie.get('appVersion')
  if (cookieVersionMatched) {
    return cookieVersionMatched
  }

  const uaVersionMatched = navigator.userAgent.match(/appVersion\/(\d+\.\d+\.\d+)/)
  if (uaVersionMatched) {
    return uaVersionMatched[1]
  }

  return '1.0.0'
}

const getOldStore = () => {
  if (isIOS()) return 'apple'
  else if (/gp-/.test(navigator.userAgent)) return 'gp'
  return 'apk'
}

/**
 * Retrieves application information based on the environment and platform.
 *
 * @returns {Object} An object containing application information.
 * @returns {string} return.region - The region of the application.
 * @returns {boolean} return.appWebview - Indicates if the app is running in a webview.
 * @returns {string} return.appName - The name of the application.
 * @returns {string} return.appVersion - The version of the application.
 * @returns {string} return.platform - The platform of the application (e.g., 'ios', 'android', 'web', 'wap').
 * @returns {string} return.store - The store from which the application was installed.
 */
export const getAppInfo = () => {
  const info = {
    region: REGION
  }

  const wapVersion = document.querySelector('meta[name="msversion"]')?.content || ''

  // new app version
  if (window.msUserAgent) {
    const { appName, appVersion, platform, store, theme } = window.msUserAgent
    return {
      ...info,
      appWebview: true,
      appName,
      appVersion,
      wapVersion,
      platform,
      store,
      theme
    }
  }

  // old app version
  if (isOldMsportApp() || isOldGamesApp() || isOldCodeApp()) {
    return {
      ...info,
      appWebview: true,
      appName: getOldAppName(),
      appVersion: getOldAppVersion(),
      wapVersion,
      platform: isIOS() ? 'ios' : 'android',
      store: getOldStore()
    }
  }

  // browser
  const appName = process.env.PLATFORM === 'desktop' ? 'web' : 'wap'
  return {
    ...info,
    appWebview: false,
    appName,
    appVersion: '',
    wapVersion,
    platform: appName,
    store: 'browser'
  }
}

export const appInfo = getAppInfo()

// msport aviator 独立 app
export const isGamesApp = () => appInfo.appName === 'msportGames'

// msport aviator 独立 ios app
export const isIosGamesApp = () => isGamesApp() && appInfo.platform === 'ios'

// 检测 app
export const isApp = () => appInfo.appWebview

// 检测 google play android app
export const isAppOfGP = () => appInfo.store === 'gp'

// 检测 ios msport app
export const isIosApp = () => appInfo.appName === 'msport' && appInfo.platform === 'ios'
